import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useReveal } from '../hooks/useReveal';
import {
  CheckCircle, XCircle, ArrowRight, Star,
  ChevronDown, ChevronUp, MessageCircle, Zap
} from 'lucide-react';

const included = [
  { text: 'Custom designed website (up to 5 pages)', yes: true },
  { text: 'Mobile & tablet responsive design', yes: true },
  { text: 'Pakistani web hosting (99.9% uptime)', yes: true },
  { text: 'Free SSL certificate (https)', yes: true },
  { text: 'Monthly content updates (unlimited requests)', yes: true },
  { text: 'Basic SEO setup & meta tags', yes: true },
  { text: 'Google Analytics integration', yes: true },
  { text: 'Contact form with email notifications', yes: true },
  { text: 'WhatsApp chat button', yes: true },
  { text: 'Google Maps integration', yes: true },
  { text: 'Social media links', yes: true },
  { text: 'WhatsApp & call support (6 days/week)', yes: true },
  { text: 'Daily website backups', yes: true },
  { text: 'Speed optimisation', yes: true },
  { text: 'Mobile app development', yes: false },
  { text: 'E-commerce / online store', yes: false },
  { text: 'Custom web application', yes: false },
];

const faqs = [
  { q: 'Is there a setup fee?', a: 'No setup fee, no hidden charges. You pay Rs. 5,000 on the 1st of every month. That\'s it.' },
  { q: 'What payment methods do you accept?', a: 'We accept bank transfer, EasyPaisa, JazzCash, and credit/debit cards.' },
  { q: 'How do I provide my website content?', a: 'After signing up, we\'ll send you a simple Google Form to fill in your business details, text content, and photos. No tech knowledge required.' },
  { q: 'Can I add more pages later?', a: 'Yes. Additional pages beyond 5 can be added for a one-time fee of Rs. 3,000 per page.' },
  { q: 'What happens if I cancel?', a: 'Cancel anytime with 30 days notice. Your website stays live until the end of your paid period.' },
  { q: 'Do you make changes quickly?', a: 'Yes. Most update requests are completed within 24 hours. Urgent changes (like changing contact number) are done same day.' },
  { q: 'Can I upgrade to e-commerce later?', a: 'Absolutely. We can discuss a custom quote for adding an online store to your existing website.' },
];

const compare = [
  { feature: 'Upfront cost', webwalay: 'Rs. 0', agency: 'Rs. 50,000–200,000', freelancer: 'Rs. 15,000–80,000' },
  { feature: 'Monthly cost', webwalay: 'Rs. 5,000', agency: 'Rs. 0 (but no support)', freelancer: 'Rs. 0 (but no support)' },
  { feature: 'Hosting included', webwalay: '✅', agency: '❌ Extra cost', freelancer: '❌ You manage' },
  { feature: 'Monthly updates', webwalay: '✅ Unlimited', agency: '❌ Extra billing', freelancer: '❌ Hard to reach' },
  { feature: 'SSL security', webwalay: '✅ Free', agency: '✅', freelancer: '❌ Usually extra' },
  { feature: 'Support', webwalay: '✅ WhatsApp 6d/wk', agency: '⚠️ Slow email', freelancer: '⚠️ Hit or miss' },
  { feature: 'Contract', webwalay: '❌ None — cancel anytime', agency: '✅ Usually locked in', freelancer: '⚠️ Varies' },
  { feature: 'Launch time', webwalay: '7 days', agency: '4–12 weeks', freelancer: '2–8 weeks' },
];

function FAQ() {
  const [open, setOpen] = useState(null);
  return (
    <section id="faq" style={{ padding: '80px 0' }}>
      <div style={{ maxWidth: '720px', margin: '0 auto', padding: '0 24px' }}>
        <h2 className="syne reveal" style={{ fontSize: 'clamp(26px,4vw,42px)', fontWeight: 800, letterSpacing: '-1px', marginBottom: '36px', textAlign: 'center' }}>
          Pricing <span className="lime-text">FAQs</span>
        </h2>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {faqs.map((f, i) => (
            <div key={i} className="card reveal" style={{ transitionDelay: `${i * 0.05}s`, overflow: 'hidden' }}>
              <button onClick={() => setOpen(open === i ? null : i)}
                style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 22px', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', gap: '12px' }}>
                <span style={{ fontFamily: 'Syne,sans-serif', fontSize: '15px', fontWeight: 700, color: 'var(--text)' }}>{f.q}</span>
                <span style={{ color: 'var(--lime-dark)', flexShrink: 0 }}>{open === i ? <ChevronUp size={17} /> : <ChevronDown size={17} />}</span>
              </button>
              {open === i && (
                <div style={{ padding: '0 22px 18px', fontSize: '14px', color: 'var(--text2)', lineHeight: 1.8, animation: 'fadeUp 0.3s ease' }}>
                  {f.a}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function Pricing() {
  const [width, setWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handleResize = () => setWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useReveal();

  return (
    <div style={{ paddingTop: '160px' }}>

      {/* Hero */}
      <section style={{ padding: '80px 24px 60px', textAlign: 'center', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(ellipse at 50% 0%, rgba(120,81,160,0.06), transparent 65%)', pointerEvents: 'none' }} />
        <div style={{ position: 'relative', maxWidth: '720px', margin: '0 auto' }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '6px 16px', background: 'rgba(120,81,160,0.07)', border: '1px solid rgba(120,81,160,0.2)', borderRadius: '20px', fontSize: '12px', color: 'var(--lime-dark)', marginBottom: '24px', fontWeight: 600 }}>
            <Zap size={13} /> Simple, transparent pricing
          </div>
          <h1 className="syne" style={{ fontSize: 'clamp(36px,6vw,72px)', fontWeight: 800, letterSpacing: '-2px', lineHeight: 1.05, marginBottom: '16px' }}>
            One plan.<br /><span className="lime-text">Everything included.</span>
          </h1>
          <p style={{ fontSize: '16px', color: 'var(--text2)', lineHeight: 1.8, marginBottom: '12px' }}>
            No hidden fees. No complicated tiers. No surprises.
          </p>
          <p style={{ fontSize: '14px', color: 'var(--text3)' }}>
            Just Rs. 5,000/month for a complete professional web presence.
          </p>
        </div>
      </section>

      {/* Main Pricing Card */}
      <section style={{ padding: '0 24px 80px' }}>
        <div style={{ maxWidth: '960px', margin: '0 auto' }}>
          <div className="reveal-scale" style={{
            background: 'var(--bg3)', border: '1px solid rgba(120,81,160,0.2)',
            borderRadius: '28px', overflow: 'hidden',
            boxShadow: '0 2px 40px rgba(0,0,0,0.07)',
          }}>

            {/* Top banner */}
            <div style={{ background: 'var(--lime)', padding: width > 640 ? '14px 32px' : '12px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontFamily: 'Syne,sans-serif', fontWeight: 800, fontSize: width > 640 ? '14px' : '12px', color: '#FFFFFF' }}>
                <Zap size={width > 640 ? 16 : 14} /> Most popular plan in Pakistan
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                {[1,2,3,4,5].map(i => <Star key={i} size={width > 640 ? 14 : 12} fill="#FFFFFF" color="#FFFFFF" />)}
                <span style={{ fontSize: width > 640 ? '12px' : '11px', color: '#FFFFFF', fontWeight: 600, marginLeft: '6px' }}>500+ happy businesses</span>
              </div>
            </div>

            <div style={{ padding: width > 640 ? '48px' : '28px', display: 'grid', gridTemplateColumns: width > 768 ? '1fr 1fr' : '1fr', gap: width > 768 ? '56px' : '36px' }}>

              {/* Left: price + features */}
              <div>
                <div style={{ marginBottom: '32px' }}>
                  <div style={{ fontFamily: 'Syne,sans-serif', fontSize: '13px', color: 'var(--text3)', letterSpacing: '2px', textTransform: 'uppercase', marginBottom: '12px' }}>Monthly Subscription</div>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', marginBottom: '6px' }}>
                    <span style={{ fontFamily: 'Syne,sans-serif', fontSize: width > 640 ? '18px' : '14px', color: 'var(--lime-dark)', fontWeight: 700, marginTop: width > 640 ? '14px' : '8px' }}>Rs.</span>
                    <span style={{ fontFamily: 'Syne,sans-serif', fontSize: width > 640 ? '80px' : '48px', fontWeight: 800, color: 'var(--lime-dark)', lineHeight: 1, letterSpacing: width > 640 ? '-4px' : '-2px' }}>5,000</span>
                    <span style={{ fontFamily: 'Syne,sans-serif', fontSize: width > 640 ? '18px' : '14px', color: 'var(--text3)', fontWeight: 500, marginTop: width > 640 ? '56px' : '32px' }}>/mo</span>
                  </div>
                  <div style={{ fontSize: '14px', color: 'var(--text3)' }}>
                    Billed monthly · No setup fee · Cancel anytime
                  </div>
                </div>

                {/* Value comparison */}
                <div style={{ background: 'rgba(120,81,160,0.05)', border: '1px solid rgba(120,81,160,0.15)', borderRadius: '14px', padding: width > 640 ? '18px 20px' : '14px 16px', marginBottom: '28px' }}>
                  <div style={{ fontSize: '12px', color: 'var(--lime-dark)', fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', marginBottom: '12px' }}>Compare the cost</div>
                  {[
                    { label: 'Typical agency upfront', val: 'Rs. 80,000+', strike: true },
                    { label: 'Freelancer (no support)', val: 'Rs. 25,000+', strike: true },
                    { label: 'WebWalay (everything)', val: 'Rs. 5,000/mo', strike: false, highlight: true },
                  ].map((r, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 0', borderBottom: i < 2 ? '1px solid rgba(0,0,0,0.06)' : 'none' }}>
                      <span style={{ fontSize: width > 640 ? '13px' : '12px', color: 'var(--text3)' }}>{r.label}</span>
                      <span style={{ fontSize: width > 640 ? '13px' : '12px', fontWeight: 700, color: r.highlight ? 'var(--lime-dark)' : 'var(--text3)', textDecoration: r.strike ? 'line-through' : 'none' }}>{r.val}</span>
                    </div>
                  ))}
                </div>

                {/* CTA buttons */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  <a href="https://wa.me/923000000000?text=Hi! I want to get started with WebWalay" target="_blank" rel="noreferrer"
                    className="btn-lime" style={{ justifyContent: 'center', fontSize: width > 640 ? '15px' : '14px', padding: width > 640 ? '15px' : '12px', width: '100%', boxSizing: 'border-box' }}>
                    Get Started Now <ArrowRight size={width > 640 ? 16 : 14} />
                  </a>
                  <a href="https://wa.me/923000000000?text=Hi! I have some questions about WebWalay" target="_blank" rel="noreferrer"
                    className="btn-outline" style={{ justifyContent: 'center', width: '100%', boxSizing: 'border-box' }}>
                    <MessageCircle size={width > 640 ? 15 : 13} /> Ask Us on WhatsApp
                  </a>
                </div>
              </div>

              {/* Right: what's included */}
              <div>
                <div style={{ fontFamily: 'Syne,sans-serif', fontSize: '15px', fontWeight: 700, color: 'var(--text)', marginBottom: '20px' }}>
                  What's included
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {included.map((item, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                      {item.yes
                        ? <CheckCircle size={16} color="var(--lime)" style={{ flexShrink: 0, marginTop: '1px' }} />
                        : <XCircle size={16} color="var(--text3)" style={{ flexShrink: 0, marginTop: '1px' }} />
                      }
                      <span style={{ fontSize: '13px', color: item.yes ? 'var(--text2)' : 'var(--text3)', textDecoration: item.yes ? 'none' : 'none' }}>
                        {item.text}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Comparison Table */}
      <section id="included" style={{ padding: '0 24px 80px', background: 'var(--bg2)' }}>
        <div style={{ maxWidth: '900px', margin: '0 auto', paddingTop: '80px' }}>
          <h2 className="syne reveal" style={{ fontSize: 'clamp(26px,4vw,44px)', fontWeight: 800, letterSpacing: '-1px', marginBottom: '36px', textAlign: 'center' }}>
            WebWalay vs <span className="lime-text">the alternatives</span>
          </h2>

          <div className="reveal" style={{ background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: '20px', overflow: width <= 640 ? 'auto' : 'hidden' }}>
            {/* Table header */}
            <div style={{ display: 'grid', gridTemplateColumns: width > 640 ? '2fr 1fr 1fr 1fr' : '200px 120px 120px 140px', background: 'var(--bg4)', padding: '16px 24px', minWidth: width <= 640 ? '620px' : 'auto' }}>
              <div style={{ fontSize: '12px', color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '1px' }}>Feature</div>
              {['WebWalay', 'Agency', 'Freelancer'].map((h, i) => (
                <div key={h} style={{ fontSize: '13px', fontWeight: 700, color: i === 0 ? 'var(--lime-dark)' : 'var(--text2)', textAlign: 'center', fontFamily: 'Syne,sans-serif' }}>{h}</div>
              ))}
            </div>
            {compare.map((row, i) => (
              <div key={i} style={{ display: 'grid', gridTemplateColumns: width > 640 ? '2fr 1fr 1fr 1fr' : '200px 120px 120px 140px', padding: '14px 24px', borderTop: '1px solid var(--border)', alignItems: 'center', minWidth: width <= 640 ? '620px' : 'auto' }}>
                <div style={{ fontSize: '13px', color: 'var(--text2)' }}>{row.feature}</div>
                <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--lime-dark)', textAlign: 'center' }}>{row.webwalay}</div>
                <div style={{ fontSize: '12px', color: 'var(--text3)', textAlign: 'center' }}>{row.agency}</div>
                <div style={{ fontSize: '12px', color: 'var(--text3)', textAlign: 'center' }}>{row.freelancer}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process on pricing page */}
      <section style={{ padding: '80px 24px' }}>
        <div style={{ maxWidth: '900px', margin: '0 auto' }}>
          <h2 className="syne reveal" style={{ fontSize: 'clamp(26px,4vw,44px)', fontWeight: 800, letterSpacing: '-1px', marginBottom: '48px', textAlign: 'center' }}>
            Getting started takes<br /><span className="lime-text">less than 5 minutes.</span>
          </h2>
          <div style={{
            display: 'grid',
            gridTemplateColumns: width > 1024 ? 'repeat(3, 1fr)' : width > 767 ? 'repeat(2, 1fr)' : '1fr',
            gap: '24px',
          }}>
            {[
              { step: '01', emoji: '📝', title: 'Fill the form', desc: 'Tell us about your business. Name, services, photos, contact info. Takes 5 minutes.' },
              { step: '02', emoji: '✅', title: 'We build it', desc: 'Our team designs and builds your website. You review and approve before it goes live.' },
              { step: '03', emoji: '🚀', title: 'Go live!', desc: 'Your website launches within 7 days. Sit back — we handle everything from here.' },
            ].map((s, i) => (
              <div key={i} className="card reveal" style={{ padding: '28px', textAlign: 'center', transitionDelay: `${i * 0.1}s` }}>
                <div style={{ fontSize: '40px', marginBottom: '12px' }}>{s.emoji}</div>
                <div style={{ fontFamily: 'Syne,sans-serif', fontSize: '11px', letterSpacing: '2px', color: 'var(--lime-dark)', marginBottom: '8px', fontWeight: 700 }}>STEP {s.step}</div>
                <h3 style={{ fontFamily: 'Syne,sans-serif', fontSize: '17px', fontWeight: 700, color: 'var(--text)', marginBottom: '10px' }}>{s.title}</h3>
                <p style={{ fontSize: '13px', color: 'var(--text2)', lineHeight: 1.7 }}>{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <FAQ />

      {/* Final CTA */}
      <section style={{ padding: '0 24px 80px' }}>
        <div style={{ maxWidth: '680px', margin: '0 auto', textAlign: 'center' }}>
          <div className="reveal-scale" style={{ background: 'var(--bg3)', border: '1px solid rgba(120,81,160,0.18)', borderRadius: '24px', padding: '52px 40px' }}>
            <div style={{ fontSize: '40px', marginBottom: '16px' }}>🚀</div>
            <h2 className="syne" style={{ fontSize: 'clamp(24px,4vw,40px)', fontWeight: 800, letterSpacing: '-1px', marginBottom: '14px' }}>
              Ready to get online?
            </h2>
            <p style={{ fontSize: '15px', color: 'var(--text2)', lineHeight: 1.8, marginBottom: '28px' }}>
              WhatsApp us right now and we'll have your website live within the week.
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <a href="https://wa.me/923000000000?text=Hi! I want to start with WebWalay" target="_blank" rel="noreferrer"
                className="btn-lime" style={{ fontSize: '15px', padding: '14px 28px' }}>
                Start on WhatsApp <ArrowRight size={16} />
              </a>
              <Link to="/" className="btn-outline" style={{ fontSize: '15px', padding: '14px 28px' }}>
                ← Back to Home
              </Link>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
}
